// Definition-side helpers for the Funnels tab: turning a stored funnel into the
// shape the form edits, back into the shape the API stores, and the bounds the
// form has to respect. The API validates all of this again — these keep the
// user from being told off by the server for something the form could have
// prevented.

// The semantic steps the API resolves against the domain's own action names at
// query time (constants/funnel-event-mappings.js on the API side). Steps with no
// semantic meaning carry literal `actions` instead, taken from the domain's
// discovered event actions.
export const FUNNEL_EVENT_MAPPINGS = ['purchase', 'refund', 'cartAdd', 'cartCreate'];

// Mirrors MIN_STEPS/MAX_STEPS/MAX_FUNNELS in the API's api/admin/funnels.js.
export const MIN_FUNNEL_STEPS = 2;
export const MAX_FUNNEL_STEPS = 6;
export const MAX_FUNNELS = 5;

// A step carries exactly one of eventMapping / actions. The form holds both
// alongside a `kind` discriminator so switching back and forth does not lose
// what was typed; draftToFunnelPayload drops the unused one.
export const STEP_KIND_EVENT_MAPPING = 'eventMapping';
export const STEP_KIND_ACTIONS = 'actions';

/**
 * A new, empty step for the builder. The event is left unselected: pre-selecting
 * one lets a user who filled in only the step names save a funnel whose steps
 * all measure the same event, which reports a meaningless 100% conversion.
 * @returns {{name: string, kind: string, eventMapping: string|null, actions: string[]}}
 */
export const blankFunnelStep = () => ({
  name: '',
  kind: STEP_KIND_EVENT_MAPPING,
  eventMapping: null,
  actions: [],
});

/**
 * The editable form state for a funnel, or for a new one when given nothing.
 * @param {{name: string, segmentId: number|null, steps: Array<object>}} [funnel]
 * @returns {{name: string, segmentId: number|null, steps: Array<object>}}
 */
export const funnelToDraft = (funnel) => ({
  name: funnel?.name || '',
  segmentId: funnel?.segmentId ?? null,
  steps: funnel
    ? funnel.steps.map((step) => ({
        name: step.name,
        kind: step.eventMapping ? STEP_KIND_EVENT_MAPPING : STEP_KIND_ACTIONS,
        eventMapping: step.eventMapping || null,
        actions: step.actions || [],
      }))
    : [blankFunnelStep(), blankFunnelStep()],
});

/**
 * The POST/PUT body for a draft.
 * @param {{name: string, segmentId: number|null, steps: Array<object>}} draft
 * @returns {{name: string, segmentId: number|null, steps: Array<object>}}
 */
export const draftToFunnelPayload = (draft) => ({
  name: draft.name.trim(),
  // An explicit null clears the audience filter; the API leaves it alone only
  // when the key is absent, which is not what "(None)" means here.
  segmentId: draft.segmentId ?? null,
  steps: draft.steps.map((step) =>
    step.kind === STEP_KIND_EVENT_MAPPING
      ? { name: step.name.trim(), eventMapping: step.eventMapping }
      : { name: step.name.trim(), actions: step.actions }
  ),
});

/**
 * First reason a draft cannot be saved, or null when it can. The step count is
 * not checked here: the builder's add/remove controls are disabled at the
 * bounds, so it cannot leave them.
 * @param {{name: string, steps: Array<object>}} draft
 * @returns {string|null}
 */
export const validateFunnelDraft = (draft) => {
  if (!draft.name.trim()) {
    return 'A funnel needs a name';
  }
  for (const [index, step] of draft.steps.entries()) {
    if (!step.name.trim()) {
      return `Step ${index + 1} needs a name`;
    }
    if (step.kind === STEP_KIND_ACTIONS && step.actions.length === 0) {
      return `Step ${index + 1} needs at least one event action`;
    }
    if (step.kind === STEP_KIND_EVENT_MAPPING && !step.eventMapping) {
      return `Step ${index + 1} needs a standard event`;
    }
  }
  return null;
};

/**
 * Steps with the one at `index` moved by `offset` places. Callers keep the move
 * in range by disabling the control at either end.
 * @param {Array<object>} steps
 * @param {number} index
 * @param {number} offset - -1 to move up, 1 to move down
 * @returns {Array<object>}
 */
export const moveFunnelStep = (steps, index, offset) => {
  const moved = [...steps];
  const [step] = moved.splice(index, 1);
  moved.splice(index + offset, 0, step);
  return moved;
};

/**
 * Name to show for a funnel's audience filter. The id is shown on its own when
 * the segment is not in the loaded list — it was deleted, or its scope changed
 * out from under the funnel.
 * @param {Array<{id: number, name: string}>} [segments]
 * @param {number} segmentId
 * @returns {string}
 */
export const funnelSegmentLabel = (segments, segmentId) =>
  (segments || []).find((segment) => segment.id === segmentId)?.name || `segment ${segmentId}`;

/**
 * The funnel stats response as FunnelChart steps. Every step is present in
 * definition order, including at count 0, so nothing has to be filled in.
 * @param {{steps: Array<{name: string, count: number, percentOfPrevious: number|null}>}} stats
 * @returns {Array<{label: string, value: number, percentOfPrevious: number|null}>}
 */
export const funnelStatsToChartSteps = (stats) =>
  stats.steps.map((step) => ({
    label: step.name,
    value: step.count,
    percentOfPrevious: step.percentOfPrevious,
  }));

// Read-side, unlike everything above: both places that DRAW a funnel describe
// what it measured, and they described it differently. One sentence, one home —
// the Funnels tab is where At a Glance's own "Edit funnels" link lands, so a
// reader could otherwise see the same funnel described two ways in two clicks.

/**
 * The one sentence describing what a funnel measured.
 *
 * THE RANGE IS THE WINDOW. An earlier version of this named
 * `conversionWindowHours` as a separate constraint — ", within 14 days" — on the
 * argument that the range decides which events are in scope while the window
 * decides how long a profile has. That is wrong, and the API says so at
 * helpers/analytics/funnel-reporter.js:49-65: the window it hands windowFunnel IS
 * the range's duration, and since the WHERE clause already confines every scanned
 * event to that same range, the window argument "can never actually reject a
 * chain". The field is reported so the UI can state the range it was computed
 * over, explicitly "not a claim that a narrower window was enforced" — which is
 * what naming it as a window did. A genuinely configurable per-funnel window is a
 * real concept the API calls out as deliberately not in v1; when it exists there
 * will be a second number to name, and it will not be this one.
 *
 * So the sentence says the period, which is the only bound there is.
 *
 * Three population states, not two. `segmentId === null` is the API actively
 * reporting no audience filter, which is what licenses "everyone"; an ABSENT
 * `segmentId` is the API saying nothing, and "everyone" would then be a positive
 * claim made on a guess — false for a funnel that is in fact segmented. The
 * untold case gets the neutral wording instead.
 * @param {object} [funnel] - read for `segmentId`, which may be absent
 * @returns {string}
 */
export const funnelMeasurementCaption = (funnel = {}) => {
  if (funnel.segmentId) {
    return 'Profiles in this funnel’s segment who completed these steps in order, within the selected period.';
  }
  if (funnel.segmentId === null) {
    return 'Everyone who visited your site and completed these steps in order, within the selected period.';
  }
  return 'Profiles who completed these steps in order, within the selected period.';
};
